"use client";

import { c as _c } from "react/compiler-runtime";
import * as TabsPrimitives from "@radix-ui/react-tabs";
import React from "react";
import { cx, focusRing } from "@/lib/tailwind-utils";
const Tabs = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TabsPrimitives.Root {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
Tabs.displayName = "Tabs";
type TabsListVariant = "line" | "solid";
const TabsListVariantContext = React.createContext<TabsListVariant>("solid");
interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitives.List> {
  variant?: TabsListVariant;
}
const variantStyles: Record<TabsListVariant, string> = {
  line: cx(
  // base
  "flex items-center justify-start border-b",
  // border color
  "border-border"),
  solid: cx(
  // base
  "inline-flex items-center justify-center rounded-md p-1",
  // border color
  "border-border",
  // background color
  "bg-muted")
};
const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitives.List>, TabsListProps>((t0, forwardedRef) => {
  const $ = _c(16);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      variant: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "solid" : t1;
  const t2 = variantStyles[variant];
  let t3;
  if ($[5] !== className || $[6] !== t2) {
    t3 = cx(t2, className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children || $[9] !== variant) {
    t4 = <TabsListVariantContext.Provider value={variant}>{children}</TabsListVariantContext.Provider>;
    $[8] = children;
    $[9] = variant;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== forwardedRef || $[12] !== props || $[13] !== t3 || $[14] !== t4) {
    t5 = <TabsPrimitives.List ref={forwardedRef} className={t3} {...props}>{t4}</TabsPrimitives.List>;
    $[11] = forwardedRef;
    $[12] = props;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
});
TabsList.displayName = "TabsList";
function getVariantStyles(tabVariant: TabsListVariant) {
  switch (tabVariant) {
    case "line":
      return cx(
      // base
      "-mb-px items-center justify-center whitespace-nowrap border-b-2 border-transparent px-3 pb-3 text-body-strong transition-all",
      // text color
      "text-muted-foreground",
      // hover
      "hover:text-foreground-secondary",
      // border hover
      "hover:border-border",
      // selected
      "data-[state=active]:border-primary data-[state=active]:text-foreground",
      // disabled
      "disabled:pointer-events-none", "disabled:text-muted-foreground");
    case "solid":
      return cx(
      // base
      "inline-flex items-center justify-center whitespace-nowrap rounded px-3 py-1 transition-all text-body-strong",
      // text color
      "text-muted-foreground",
      // hover
      "hover:text-foreground-secondary",
      // selected
      "data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow",
      // disabled
      "disabled:pointer-events-none disabled:text-foreground-muted");
  }
}
const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitives.Trigger>, React.ComponentPropsWithoutRef<typeof TabsPrimitives.Trigger>>((t0, forwardedRef) => {
  const $ = _c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const variant = React.useContext(TabsListVariantContext);
  let t1;
  if ($[4] !== className || $[5] !== variant) {
    t1 = cx(getVariantStyles(variant), focusRing, className);
    $[4] = className;
    $[5] = variant;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== forwardedRef || $[9] !== props || $[10] !== t1) {
    t2 = <TabsPrimitives.Trigger ref={forwardedRef} className={t1} {...props}>{children}</TabsPrimitives.Trigger>;
    $[7] = children;
    $[8] = forwardedRef;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
});
TabsTrigger.displayName = "TabsTrigger";
const TabsContent = React.forwardRef<React.ElementRef<typeof TabsPrimitives.Content>, React.ComponentPropsWithoutRef<typeof TabsPrimitives.Content>>((t0, forwardedRef) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("outline-none", focusRing, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== forwardedRef || $[6] !== props || $[7] !== t1) {
    t2 = <TabsPrimitives.Content ref={forwardedRef} className={t1} {...props} />;
    $[5] = forwardedRef;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
TabsContent.displayName = "TabsContent";
export { Tabs, TabsContent, TabsList, TabsTrigger };